import {ISubscription} from 'app/models/subscription'
import {ITenant} from 'app/models/tenant'
import {IUser, IUserProject, IUserSubscription} from 'app/models/user'
import {LOCAL_CURRENT_TENANT_ID, LOCAL_CURRENT_TENANT_NAME, UserProfile} from 'app/modules/auth'
import {JsonObject} from 'utilities/types'
import {CBaseRecord} from './_base'
import {CSavedSearch} from './saved_search'

export class CSubscription extends CBaseRecord<ISubscription> implements ISubscription {
  static record_name = 'subscription'

  name
  is_tenant_admin
  tenant
  users
  max_users
  features
  package_id

  constructor(data: ISubscription & JsonObject) {
    super(data)
    this.name = data.name
    this.is_tenant_admin = data.is_tenant_admin
    this.tenant = data.tenant
    this.package_id = data.package_id
    this.users = data.users?.map((x) => new CUser(x as unknown as IUser & JsonObject))
    this.max_users = data.max_users
    this.features = data.features
  }
}
export class CTenant extends CBaseRecord<ITenant> implements ITenant {
  static external_id = 'tenant_id'
  static record_name = 'tenant'
  static children = {
    subscriptions: CSubscription,
  }

  id
  name
  description
  subscriptions
  is_personal

  constructor(data: ITenant & JsonObject) {
    super(data)
    this.id = data.id
    this.name = data.name
    this.description = data.description
    this.is_personal = data.is_personal
    this.subscriptions = data.subscriptions?.map(
      (x) => new CSubscription(x as unknown as ISubscription & JsonObject)
    )
  }
}

export class CUserSubscription extends CSubscription implements IUserSubscription {
  __role

  constructor(data: IUserSubscription & JsonObject) {
    super(data)
    this.__role = data.__role
  }
}

export class CUser extends CBaseRecord<IUser> implements IUser {
  static record_name = 'user'
  static children = {
    subscriptions: CSubscription,
  }

  first_name
  last_name
  email
  created_at
  updated_at
  recent_searches
  profile: UserProfile
  is_active
  is_global_admin
  subscriptions
  personal_tenant_id
  features
  _subs_by_tenant_id: Record<string, CUserSubscription[]>

  constructor(data: IUser & JsonObject) {
    super(data)
    this.first_name = data.first_name
    this.last_name = data.last_name
    this.email = data.email
    this.created_at = data.created_at
    this.updated_at = data.updated_at
    this.recent_searches = data.recent_searches?.map(
      (x) => new CSavedSearch(x as unknown as CSavedSearch & JsonObject)
    )
    this.profile = new UserProfile(data.profile ?? {})
    this.is_active = data.is_active
    this.is_global_admin = data.is_global_admin
    this.subscriptions = data.subscriptions?.map(
      (x) => new CUserSubscription(x as unknown as IUserSubscription & JsonObject)
    )
    this.personal_tenant_id =
      data.subscriptions
        ?.map((x) => new CUserSubscription(x as IUserSubscription & JsonObject))
        ?.find((f) => f?.tenant?.is_personal)?.tenant?.id ?? undefined

    this.features = data.features

    this._subs_by_tenant_id = {}
    for (const sub of this.subscriptions ?? []) {
      if (sub.tenant?.id) {
        if (!this._subs_by_tenant_id[sub.tenant.id]) {
          this._subs_by_tenant_id[sub.tenant.id] = []
        }
        this._subs_by_tenant_id[sub.tenant.id].push(sub)
      }
    }
  }

  get displayName() {
    return `${this.last_name}, ${this.first_name}`
  }

  _tenantData(filter: (x: ITenant) => boolean, id_key?: string, name_key?: string) {
    const id = id_key ? localStorage.getItem(id_key) ?? undefined : undefined
    const name = name_key ? localStorage.getItem(name_key) : undefined

    //TODO //have to check this and force user out if it's not in the list so figure out how to add a tenant and update the list then the ession then the refresh
    //in order to get this to work
    //&& id in this._subs_by_tenant_id -> changed because when addign a new tenant now, it maybe not be in the list until after a refresh
    if (id !== undefined) {
      // return name ? {id, name} : {id}
      return {id: id, name: name}
    }

    for (const id in this._subs_by_tenant_id) {
      //default to the first tenant
      let tenant = this._subs_by_tenant_id[id][0].tenant
      //check number of subscriptions to see if they have another tennt besides personal tenant
      let switched = false
      if ((this.subscriptions?.length ?? 0) > 1) {
        //chek the profile
        if (this.profile.subscription_preferences?.preferred_tenant_id) {
          const found = this.subscriptions?.find(
            (f) => f.tenant?.id === this.profile.subscription_preferences?.preferred_tenant_id
          )
          if (found && found.tenant?.id) {
            tenant = this._subs_by_tenant_id[found.tenant.id][0].tenant
            switched = true
          }
        }
        //setting to the second tenant, not the personal tenant
        if (!switched) {
          this.subscriptions?.map((m) => {
            if (m.tenant?.id && m.tenant.id !== this.personal_tenant_id) {
              tenant = this._subs_by_tenant_id[m.tenant.id][0].tenant
            }
          })
        }
      }

      if (tenant && tenant.id && filter(tenant)) {
        if (id_key && name_key) {
          localStorage.setItem(id_key, tenant.id)
          localStorage.setItem(name_key, tenant.name)
        }
        return {id: tenant.id, name: tenant.name}
      }
    }

    return undefined
  }

  get activeTenantData() {
    return this._tenantData(() => true, LOCAL_CURRENT_TENANT_ID, LOCAL_CURRENT_TENANT_NAME)
  }

  get personalTenantData() {
    return this._tenantData((x) => x.is_personal)
  }

  stringify() {
    return JSON.stringify(this.export())
  }

  hasProjectFeature(tenant_id?: string) {
    return (
      this.hasFeature({feature: 'project-viewer', tenant_id}) ||
      this.hasFeature({feature: 'project-contributor', tenant_id}) ||
      this.hasFeature({feature: 'project-admin', tenant_id})
    )
  }

  hasFeature(args: {feature: string; tenant_id?: string}) {
    //TODO - can do this later
    // if (this.is_global_admin) {
    //   // Global admins can do anything to anything with anything.
    //   return true
    // }
    if (this.features === undefined) {
      throw Error('Cannot check features on a user other than yourself.')
    }
    if (this.features[args.feature] === undefined) {
      return false
    }
    const featureTenants = this.features[args.feature]

    const tenant_id = args.tenant_id ?? this.activeTenantData?.id
    if (!tenant_id) {
      return true
    }
    return featureTenants[tenant_id] !== undefined
  }

  get tenants() {
    const output = []
    for (const tenant_id in this._subs_by_tenant_id) {
      const tenant = this._subs_by_tenant_id[tenant_id][0].tenant
      if (tenant) {
        output.push(tenant)
      }
    }
    return output
  }

  hasTenantAccess(args: {tenant_id?: string; require_admin?: boolean; feature?: string} = {}) {
    if (this.is_global_admin) {
      // Global admins can do anything to anything with anything.
      return true
    }
    const tenant_id = args.tenant_id ?? this.activeTenantData?.id
    if (!tenant_id) {
      return false
    }
    const {require_admin, feature} = args

    if (feature && !this.hasFeature({feature, tenant_id})) {
      return false
    }

    const subs = this._subs_by_tenant_id[tenant_id] ?? []
    for (const sub of subs) {
      if (sub.is_tenant_admin || !require_admin) {
        return true
      }
    }
    return false
  }

  hasProjectAccess(args: {project_id: string; role?: string; feature?: string}) {
    if (this.is_global_admin) {
      // Global admins can do anything to anything with anything.
      return true
    }
    if (!this._data.projects) {
      return false
    }

    const {project_id, role, feature} = args
    let proj: IUserProject | undefined = undefined
    for (const project of this._data.projects) {
      if (project.id === project_id) {
        proj = project
      }
    }
    if (!proj) {
      return false
    }
    if (role === 'admin' && proj.__role !== 'admin') {
      return false
    }
    if (role === 'contributor' && proj.__role !== 'admin' && proj.__role !== 'contributor') {
      return false
    }
    if (
      role === 'viewer' &&
      proj.__role !== 'admin' &&
      proj.__role !== 'contributor' &&
      proj.__role !== 'viewer'
    ) {
      return false
    }
    if (feature && !this.hasFeature({tenant_id: proj.tenant_id, feature})) {
      return false
    }
    return true
  }
}
